import { useState, useCallback, useRef, useEffect } from 'react';
import useNotification from './useNotification';
import imageService from '../services/imageService';

/**
 * Custom hook for image generation and management
 * @returns {Object} Image generation methods and state
 */
export const useImage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [projectImages, setProjectImages] = useState([]);
  const [imageCategories, setImageCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const { showNotification } = useNotification();

  /**
   * Generate an image from a text prompt
   * @param {string} prompt - The text prompt describing the desired image
   * @param {Object} options - Additional options for generation
   * @returns {Promise<Object>} - The generated image data
   */
  // State for tracking in-progress image generation requests
  const [processingRequests, setProcessingRequests] = useState([]);
  const pollingRef = useRef(null);
  
  // Function to poll for image status
  const pollForImageStatus = useCallback(async (requestId) => {
    try {
      console.log(`Polling for image status, requestId: ${requestId}`);
      
      // Poll for the status of a specific image request
      const status = await imageService.checkImageStatus(requestId);
      
      console.log(`Image status for ${requestId}:`, {
        completed: status.completed,
        progress: status.progress,
        error: status.error
      });
      
      if (status.completed) {
        // Image is ready
        setProcessingRequests(prev => prev.filter(req => req.requestId !== requestId));
        
        // Update the generated images with the new image
        if (status.data && status.data.url) {
          setGeneratedImages(prevImages => {
            // Find if we already have this image
            const existingIndex = prevImages.findIndex(img => img.requestId === requestId);
            
            if (existingIndex >= 0) {
              // Replace the placeholder with the completed image
              const updatedImages = [...prevImages];
              updatedImages[existingIndex] = {
                ...status.data,
                processing: false,
                completed: true,
                requestId // Keep the requestId for reference
              };
              return updatedImages;
            } else {
              // Add as a new image if it wasn't in the list
              return [
                { 
                  ...status.data,
                  processing: false,
                  completed: true,
                  requestId // Keep the requestId for reference
                },
                ...prevImages
              ];
            }
          });
          
          showNotification('success', 'Image generation completed');
        } else {
          // No image URL in the completed status - this is an error
          setGeneratedImages(prevImages => 
            prevImages.map(img => 
              img.requestId === requestId 
                ? { 
                    ...img, 
                    processing: false,
                    error: true,
                    message: 'Image generation completed but no image was returned'
                  }
                : img
            )
          );
          
          showNotification('error', 'Image generation completed but no image was returned');
        }
        return true;
      } else if (status.error) {
        // Handle error
        setProcessingRequests(prev => prev.filter(req => req.requestId !== requestId));
        
        // Update the image placeholder with the error
        setGeneratedImages(prevImages => 
          prevImages.map(img => 
            img.requestId === requestId 
              ? { 
                  ...img, 
                  processing: false,
                  error: true,
                  message: status.error || 'Image generation failed'
                }
              : img
          )
        );
        
        setError(status.error || 'Image generation failed');
        showNotification('error', status.error || 'Image generation failed');
        return true;
      } else {
        // Still processing - update progress information
        if (status.progress !== undefined) {
          const progress = Math.min(99, status.progress); // Cap at 99% until complete
          
          // Update the image placeholder with progress
          setGeneratedImages(prevImages => 
            prevImages.map(img => 
              img.requestId === requestId 
                ? { 
                    ...img, 
                    progress: progress,
                    message: status.message || img.message || "Generating image..."
                  }
                : img
            )
          );
          
          // If progress is significant, show a notification
          if (progress >= 50 && progress % 25 === 0) {
            showNotification('info', `Image generation ${progress}% complete`);
          }
        }
      }
      return false;
    } catch (err) {
      console.error('Error polling for image status:', err);
      
      // After several errors, we may want to mark this request as failed
      return false;
    }
  }, [showNotification]);
  
  // Set up polling mechanism for processing requests
  useEffect(() => {
    const startPolling = async () => {
      console.log(`Polling for ${processingRequests.length} image requests`);
      
      if (processingRequests.length > 0) {
        // Process each request in the queue
        for (const request of processingRequests) {
          // Check if this request has been in progress for too long (over 3 minutes)
          const currentTime = new Date().getTime();
          const requestTime = new Date(request.timestamp).getTime() || currentTime;
          const timeElapsed = (currentTime - requestTime) / 1000;
          
          // Track if polling failed to throttle error notifications
          let pollingFailed = false;
          
          if (timeElapsed > 600) {  // 10 minutes timeout (increased from 3 minutes)
            console.log(`Request ${request.requestId} timed out after ${timeElapsed} seconds`);
            
            // This request has been processing for too long, mark it as failed
            setProcessingRequests(prev => prev.filter(req => req.requestId !== request.requestId));
            setGeneratedImages(prevImages => 
              prevImages.map(img => 
                (img.requestId === request.requestId) 
                  ? { 
                      ...img, 
                      processing: false, 
                      error: true, 
                      message: "Image generation timed out after 10 minutes" 
                    }
                  : img
              )
            );
            
            // Notify the user
            showNotification('error', 'Image generation timed out after 10 minutes');
          } else {
            // Update the UI with progress even before we poll
            // This gives a smoother animation effect
            setGeneratedImages(prevImages => 
              prevImages.map(img => {
                if (img.requestId === request.requestId && img.processing) {
                  // Calculate elapsed time percentage for a smoother progress animation
                  const elapsedPercentage = Math.min(85, (timeElapsed / 180) * 100);
                  const newProgress = img.progress ? 
                    Math.max(img.progress, elapsedPercentage) : 
                    elapsedPercentage;
                    
                  return { 
                    ...img,
                    progress: newProgress
                  };
                }
                return img;
              })
            );
            
            try {
              // Poll for the status of this request
              await pollForImageStatus(request.requestId);
            } catch (err) {
              console.error(`Polling failed for request ${request.requestId}:`, err);
              pollingFailed = true;
              
              // Don't mark as error immediately, just continue with polling
              // We'll retry in the next interval
            }
          }
        }
      }
      
      // Schedule next poll if we still have requests
      if (processingRequests.length > 0) {
        // Dynamic polling interval based on elapsed time
        // Start with frequent updates and slow down over time
        const oldestRequest = processingRequests[0];
        const currentTime = new Date().getTime();
        const startTime = new Date(oldestRequest.timestamp).getTime() || currentTime;
        const elapsedSec = (currentTime - startTime) / 1000;
        
        // Adjust polling frequency based on how long we've been waiting
        // - First 10 seconds: Poll every 1 second
        // - 10-30 seconds: Poll every 2 seconds
        // - 30+ seconds: Poll every 3 seconds
        let pollInterval = 750; // Default: 750ms (was 1 second) - more frequent polling for better responsiveness
        
        if (elapsedSec > 30) {
          pollInterval = 2000; // After 30 seconds: every 2 seconds (was 3 seconds)
        } else if (elapsedSec > 10) {
          pollInterval = 1500; // After 10 seconds: every 1.5 seconds (was 2 seconds)
        }
        
        console.log(`Scheduling next poll in ${pollInterval}ms for ${processingRequests.length} requests`);
        pollingRef.current = setTimeout(startPolling, pollInterval);
      } else {
        console.log('No more processing requests, stopping polling');
      }
    };
    
    // Clear any existing polling
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    
    // Start polling if we have processing requests
    if (processingRequests.length > 0) {
      console.log(`Starting polling for ${processingRequests.length} requests immediately`);
      
      // Start polling immediately for the first check
      pollingRef.current = setTimeout(startPolling, 100); // Super quick first check
    }
    
    // Cleanup on unmount
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [processingRequests, pollForImageStatus]);

  const generateImage = useCallback(async (prompt, options = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`Generating image with model: ${options.model || 'dall-e-3'} and prompt: "${prompt.substring(0, 30)}..."`);
      
      const result = await imageService.generateImage(prompt, options);
      
      // Check if this is a streaming response
      const isStreaming = result.streaming === true;
      const requestId = result.requestId;
      
      if (isStreaming && requestId) {
        console.log(`Received streaming response with requestId: ${requestId}`);
        
        // Extract images from the response
        const newImages = result.data || [];
        
        if (newImages.length === 0) {
          console.warn('No images received from image generation API');
          setLoading(false);
          return result;
        }
        
        // These are all processing images
        const processingImages = newImages.map(img => ({
          ...img,
          processing: true,
          requestId: requestId,
          progress: 0,
          message: "Starting image generation..."
        }));
        
        // Add these to our processing requests queue
        setProcessingRequests(prev => [
          ...prev,
          {
            requestId: requestId,
            timestamp: new Date().toISOString(),
            model: options.model || 'dall-e-3'
          }
        ]);
        
        // Add placeholder images to the UI
        setGeneratedImages(prevImages => [...processingImages, ...prevImages]);
        
        // Show notification that we're starting generation
        showNotification('info', 'Image generation started - this may take up to a minute');
        
        // Keep loading state active briefly while we set up polling
        setTimeout(() => setLoading(false), 500);
      } else {
        // Standard response with immediate images
        const newImages = result.data || [];
        
        // Add these images to the UI
        setGeneratedImages(prevImages => [...newImages, ...prevImages]);
        setLoading(false);
      }
      
      return result;
    } catch (err) {
      setError(err.message || 'Failed to generate image');
      showNotification('error', err.message || 'Failed to generate image');
      setLoading(false);
      throw err;
    }
  }, [showNotification]);
  
  /**
   * Generate variations of an existing image
   * @param {string} imageUrl - URL of the source image
   * @param {Object} options - Additional options for generation
   * @returns {Promise<Object>} - The generated image variations
   */
  const generateImageVariations = useCallback(async (imageUrl, options = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await imageService.generateImageVariations(imageUrl, options);
      
      // Add the new variations to the state
      const newVariations = result.data || [];
      setGeneratedImages(prevImages => [...newVariations, ...prevImages]);
      
      return result;
    } catch (err) {
      setError(err.message || 'Failed to generate image variations');
      showNotification('error', err.message || 'Failed to generate image variations');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showNotification]);
  
  /**
   * Save a generated image to a project
   * @param {string} projectId - ID of the project
   * @param {string} imageUrl - URL of the generated image
   * @param {string} prompt - Text prompt used to generate the image
   * @param {Object} metadata - Additional metadata about the image
   * @returns {Promise<Object>} - The saved image data
   */
  const saveImageToProject = useCallback(async (projectId, imageUrl, prompt, metadata = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const savedImage = await imageService.saveImageToProject(projectId, imageUrl, prompt, metadata);
      
      // Add the saved image to project images
      setProjectImages(prevImages => [savedImage, ...prevImages]);
      
      showNotification('success', 'Image saved to project successfully');
      return savedImage;
    } catch (err) {
      setError(err.message || 'Failed to save image to project');
      showNotification('error', err.message || 'Failed to save image to project');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showNotification]);
  
  /**
   * Get all images for a project
   * @param {string} projectId - ID of the project
   * @param {Object} options - Filter options (category, favorites, tag, etc.)
   * @returns {Promise<Array>} - Array of project images
   */
  const getProjectImages = useCallback(async (projectId, options = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      // Apply the selected category if not specified in options
      if (!options.category && selectedCategory && selectedCategory !== 'all') {
        if (selectedCategory === 'favorites') {
          options.favorites = true;
        } else {
          options.category = selectedCategory;
        }
      }
      
      const images = await imageService.getProjectImages(projectId, options);
      setProjectImages(images);
      return images;
    } catch (err) {
      setError(err.message || 'Failed to get project images');
      showNotification('error', err.message || 'Failed to get project images');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showNotification, selectedCategory]);
  
  /**
   * Get image categories for a project
   * @param {string} projectId - ID of the project
   * @returns {Promise<Array>} - Array of category objects with counts
   */
  const getImageCategories = useCallback(async (projectId) => {
    try {
      const categories = await imageService.getImageCategories(projectId);
      setImageCategories(categories);
      return categories;
    } catch (err) {
      console.error('Failed to get image categories:', err);
      showNotification('error', err.message || 'Failed to get image categories');
      return [];
    }
  }, [showNotification]);
  
  /**
   * Update an image's metadata
   * @param {string} imageId - ID of the image to update
   * @param {Object} updateData - Data to update (category, tags, isFavorite)
   * @returns {Promise<Object>} - Updated image data
   */
  const updateImage = useCallback(async (imageId, updateData) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedImage = await imageService.updateImage(imageId, updateData);
      
      // Update the image in the project images list
      setProjectImages(prevImages => 
        prevImages.map(img => 
          img._id === imageId ? updatedImage : img
        )
      );
      
      showNotification('success', 'Image updated successfully');
      return updatedImage;
    } catch (err) {
      setError(err.message || 'Failed to update image');
      showNotification('error', err.message || 'Failed to update image');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showNotification]);
  
  /**
   * Delete an image
   * @param {string} imageId - ID of the image to delete
   * @returns {Promise<boolean>} - True if successful
   */
  const deleteImage = useCallback(async (imageId) => {
    setLoading(true);
    setError(null);
    
    try {
      await imageService.deleteImage(imageId);
      
      // Remove the image from the project images list
      setProjectImages(prevImages => 
        prevImages.filter(img => img._id !== imageId)
      );
      
      showNotification('success', 'Image deleted successfully');
      return true;
    } catch (err) {
      setError(err.message || 'Failed to delete image');
      showNotification('error', err.message || 'Failed to delete image');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showNotification]);
  
  /**
   * Clear all generated images from state
   */
  const clearGeneratedImages = useCallback(() => {
    setGeneratedImages([]);
  }, []);

  /**
   * Edit an image using a text prompt and optional mask
   * @param {string} imageUrl - URL of the image to edit
   * @param {string} prompt - Text prompt describing the desired changes
   * @param {string|null} maskUrl - Optional URL of mask image
   * @param {Object} options - Additional options for image editing
   * @returns {Promise<Object>} - The edited image data
   */
  const editImage = useCallback(async (imageUrl, prompt, maskUrl = null, options = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await imageService.editImage(imageUrl, prompt, maskUrl, options);
      
      // Add the edited images to the state
      const newImages = result.data || [];
      setGeneratedImages(prevImages => [...newImages, ...prevImages]);
      
      return result;
    } catch (err) {
      setError(err.message || 'Failed to edit image');
      showNotification('error', err.message || 'Failed to edit image');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showNotification]);
  
  /**
   * Generate an image using reference images
   * @param {string} prompt - Text prompt describing the desired image
   * @param {Array<string>} referenceImages - Array of URLs of reference images
   * @param {Object} options - Additional options for generation
   * @returns {Promise<Object>} - The generated image data
   */
  const generateFromReferences = useCallback(async (prompt, referenceImages, options = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`Generating image with ${referenceImages.length} reference images and prompt: "${prompt.substring(0, 30)}..."`);
      
      const result = await imageService.generateFromReferences(prompt, referenceImages, options);
      
      // Check if this is a streaming response
      const isStreaming = result.streaming === true;
      const requestId = result.requestId;
      
      if (isStreaming && requestId) {
        console.log(`Received streaming response with requestId: ${requestId}`);
        
        // Extract images from the response
        const newImages = result.data || [];
        
        if (newImages.length === 0) {
          console.warn('No images received from image generation API');
          setLoading(false);
          return result;
        }
        
        // These are all processing images
        const processingImages = newImages.map(img => ({
          ...img,
          processing: true,
          requestId: requestId,
          progress: 0,
          message: "Starting image generation with references..."
        }));
        
        // Add these to our processing requests queue
        setProcessingRequests(prev => [
          ...prev,
          {
            requestId: requestId,
            timestamp: new Date().toISOString(),
            model: 'gpt-image-1'
          }
        ]);
        
        // Add placeholder images to the UI
        setGeneratedImages(prevImages => [...processingImages, ...prevImages]);
        
        // Show notification that we're starting generation
        showNotification('info', 'Image generation started - this may take up to a minute');
        
        // Keep loading state active briefly while we set up polling
        setTimeout(() => setLoading(false), 500);
      } else {
        // Standard response with immediate images
        const newImages = result.data || [];
        
        // Add these images to the UI
        setGeneratedImages(prevImages => [...newImages, ...prevImages]);
        setLoading(false);
      }
      
      return result;
    } catch (err) {
      setError(err.message || 'Failed to generate image from references');
      showNotification('error', err.message || 'Failed to generate image from references');
      setLoading(false);
      throw err;
    }
  }, [showNotification, setProcessingRequests]);

  return {
    loading,
    error,
    generatedImages,
    projectImages,
    imageCategories,
    selectedCategory,
    setSelectedCategory,
    generateImage,
    generateImageVariations,
    editImage,
    generateFromReferences,
    saveImageToProject,
    getProjectImages,
    getImageCategories,
    updateImage,
    deleteImage,
    clearGeneratedImages
  };
};

export default useImage;